// Reverse geocoding utility with caching to convert GPS coordinates to city/state
// Uses Supabase Edge Function proxy to Nominatim (OpenStreetMap) API - free, no CORS issues

import { supabase } from '../lib/supabase';

interface LocationCache {
  [key: string]: {
    city: string;
    state: string;
    timestamp: number;
  };
}

const locationCache: LocationCache = {};
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

function getCacheKey(lat: number, lng: number): string {
  // Round to 2 decimal places (~1km precision) for cache key
  return `${lat.toFixed(2)},${lng.toFixed(2)}`;
}

function shouldGeocode(lat: number, lng: number): boolean {
  const cacheKey = getCacheKey(lat, lng);
  const cached = locationCache[cacheKey];
  
  if (!cached) return true;
  
  // Check if cache is still valid
  const now = Date.now();
  if (now - cached.timestamp > CACHE_DURATION) {
    return true;
  }
  
  return false;
}

export async function reverseGeocode(lat: number, lng: number): Promise<{
  city: string;
  state: string;
  fullLocation: string;
}> {
  const cacheKey = getCacheKey(lat, lng);
  
  // Check cache first
  const cached = locationCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return {
      city: cached.city,
      state: cached.state,
      fullLocation: `${cached.city}, ${cached.state}`
    };
  }
  
  try {
    // Call Supabase Edge Function proxy
    const { data, error } = await supabase.functions.invoke('reverse-geocode', {
      body: { lat, lng }
    });
    
    if (error) throw error;
    
    if (!data.success) {
      throw new Error(data.error || 'Geocoding failed');
    }
    
    const { city, state } = data;
    
    // Cache the result
    locationCache[cacheKey] = {
      city,
      state,
      timestamp: Date.now()
    };
    
    console.log(`[Geocode] ${lat.toFixed(4)}, ${lng.toFixed(4)} → ${city}, ${state}`);
    
    return {
      city,
      state,
      fullLocation: `${city}, ${state}`
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    
    // Return cached data if available, even if expired
    if (cached) {
      return {
        city: cached.city,
        state: cached.state,
        fullLocation: `${cached.city}, ${cached.state}`
      };
    }
    
    // Fallback to coordinates
    return {
      city: 'GPS',
      state: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      fullLocation: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    };
  }
}

// Batch geocode multiple locations with rate limiting
export async function batchReverseGeocode(
  locations: Array<{ lat: number; lng: number; id: string }>
): Promise<Map<string, { city: string; state: string; fullLocation: string }>> {
  const results = new Map();
  
  for (const loc of locations) {
    // Check if we need to geocode this location
    if (!shouldGeocode(loc.lat, loc.lng)) {
      const cacheKey = getCacheKey(loc.lat, loc.lng);
      const cached = locationCache[cacheKey];
      if (cached) {
        results.set(loc.id, {
          city: cached.city,
          state: cached.state,
          fullLocation: `${cached.city}, ${cached.state}`
        });
        continue;
      }
    }
    
    // Geocode this location
    const result = await reverseGeocode(loc.lat, loc.lng);
    results.set(loc.id, result);
  }
  
  return results;
}
